import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  Stack, 
  Alert, 
  CircularProgress,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import EditIcon from '@mui/icons-material/Edit';
import SecurityIcon from '@mui/icons-material/Security';
import { useAuth } from '../../../AuthContext';
import { useFeatureAccess } from '../../../hooks/useFeatureAccess';
import { createPortalSession, openStripeUrl } from '../../../utils/subscription';
import { getAuth, updatePassword, updateEmail, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { logAnalyticsEvent } from '../../../utils/analytics';
import { useNavigate } from 'react-router-dom';
import appConfig from '../../../config';
import { CheckCircleIcon, StarIcon } from '../../../icons';
import MembershipPricingCards from '../../subscription/MembershipPricingCards';
import { alpha } from '@mui/material/styles';
import { color, radius, r } from '../../../theme/tokens';

/**
 * Consolidated Account & Subscription Section
 * Combines personal information display with subscription management
 * Following design from section 14.4 of the design document
 */
function AccountSubscriptionSection({ userData }) {
  const { currentUser } = useAuth();
  const { subscriptionStatus, isPro, isTrialing, isGrandfathered, isComplimentaryExpired, complimentaryAccess } = useFeatureAccess();
  const auth = getAuth();
  const navigate = useNavigate();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [securityModalOpen, setSecurityModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [passwordState, setPasswordState] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: ''
  });
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [securityMessage, setSecurityMessage] = useState({ type: '', text: '' });
  const [deletionEligibility, setDeletionEligibility] = useState({
    status: 'idle',
    canDelete: false,
    message: ''
  });
  
  // Require a password only when the account actually has one linked.
  // OAuth-only users (Google, Apple) have no password to verify.
  const hasPasswordProvider = auth.currentUser?.providerData.some(p => p.providerId === 'password');
  const isGoogleUser = auth.currentUser?.providerData.some(p => p.providerId === 'google.com');
  const isOAuthOnlyUser = !hasPasswordProvider;

  useEffect(() => {
    if (!deleteModalOpen) {
      setDeletionEligibility({ status: 'idle', canDelete: false, message: '' });
      return undefined;
    }

    if (!currentUser) {
      setDeletionEligibility({
        status: 'blocked',
        canDelete: false,
        message: 'You must be logged in to delete your account.'
      });
      return undefined;
    }

    let isMounted = true;
    const functions = getFunctions();
    const checkAccountDeletionEligibility = httpsCallable(functions, 'checkAccountDeletionEligibility');

    setDeletionEligibility({ status: 'checking', canDelete: false, message: '' });

    checkAccountDeletionEligibility()
      .then((result) => {
        if (!isMounted) return;

        const canDelete = result.data?.canDelete === true;
        setDeletionEligibility({
          status: canDelete ? 'allowed' : 'blocked',
          canDelete,
          message: result.data?.message || ''
        });
      })
      .catch((eligibilityError) => {
        if (!isMounted) return;

        console.error('Error checking account deletion eligibility:', eligibilityError);
        setDeletionEligibility({
          status: 'blocked',
          canDelete: false,
          message: eligibilityError.message || 'Unable to verify account deletion eligibility. Please try again.'
        });
      });

    return () => {
      isMounted = false;
    };
  }, [deleteModalOpen, currentUser]);

  /**
   * Determine the current subscription state for UI rendering
   */
  const getSubscriptionState = () => {
    if (isGrandfathered) {
      return 'grandfathered';
    }
    if (isComplimentaryExpired()) {
      return 'complimentary_expired';
    }
    if (!subscriptionStatus || subscriptionStatus.status === 'none') {
      return 'free';
    }
    if (['trialing', 'active'].includes(subscriptionStatus.status)) {
      return 'pro';
    }
    return 'issue'; // past_due, canceled, etc.
  };

  // Complimentary access dates come from the user doc's end-audit fields,
  // never from hardcoded copy.
  const formatPlainDate = (date) => {
    try {
      if (date instanceof Date && !Number.isNaN(date.getTime())) return date.toLocaleDateString();
    } catch (_) {}
    return '';
  };
  const complimentaryEndedOn = formatPlainDate(complimentaryAccess?.endedAt);
  const complimentaryUntil = formatPlainDate(complimentaryAccess?.until);

  /**
   * Check whether backend account deletion preflight should block the UI.
   */
  const hasBlockingSubscriptionForDeletion = () => {
    return deleteModalOpen && !deletionEligibility.canDelete;
  };

  const isCheckingDeletionEligibility =
    deletionEligibility.status === 'checking' ||
    (deleteModalOpen && deletionEligibility.status === 'idle');

  /**
   * Handle opening subscription management portal
   */
  const handleManageSubscription = async () => {
    if (!currentUser) return;

    if (subscriptionState === 'issue') {
      navigate('/subscribe', { state: { from: { pathname: '/profile' } } });
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const portalUrl = await createPortalSession(currentUser.uid);
      openStripeUrl(portalUrl);
    } catch (err) {
      console.error('Error creating portal session:', err);
      setError('Failed to open subscription management. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Prefer epoch seconds when available; fall back to ISO string
  const formatSubscriptionDate = (secondsValue, isoValue) => {
    try {
      if (secondsValue && Number.isFinite(secondsValue)) {
        const ms = secondsValue > 1e12 ? secondsValue : secondsValue * 1000;
        const d = new Date(ms);
        if (!isNaN(d.getTime())) return d.toLocaleDateString();
      }
      if (isoValue) {
        const d = new Date(isoValue);
        if (!isNaN(d.getTime())) return d.toLocaleDateString();
      }
    } catch (_) {}
    return '';
  };

  /**
   * Handle password change
   */
  const handlePasswordChange = async () => {
    if (passwordState.newPassword !== passwordState.confirmNewPassword) {
      setSecurityMessage({ type: 'error', text: 'New passwords do not match.' });
      return;
    }
    setIsProcessing(true);
    setSecurityMessage({ type: '', text: '' });

    try {
      const user = auth.currentUser;
      const credential = EmailAuthProvider.credential(user.email, passwordState.currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, passwordState.newPassword);
      
      setSecurityMessage({ type: 'success', text: 'Password updated successfully.' });
      logAnalyticsEvent('change_password_success');
      setTimeout(() => {
        setSecurityModalOpen(false);
        setPasswordState({ currentPassword: '', newPassword: '', confirmNewPassword: '' });
      }, 1500);

    } catch (error) {
      setSecurityMessage({ type: 'error', text: 'Failed to update password. Please check your current password.' });
      logAnalyticsEvent('change_password_failure', { error_code: error.code });
      console.error(error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteAccount = async () => {
    setIsProcessing(true);
    setSecurityMessage({ type: '', text: '' });
    
    try {
      const user = auth.currentUser;

      // Password requirement check (for email/password users)
      if (hasPasswordProvider && !deletePassword) {
        setSecurityMessage({ type: 'error', text: 'Password is required to delete your account.' });
        setIsProcessing(false);
        return;
      }

      // Re-authenticate if using email/password
      if (hasPasswordProvider) {
        const credential = EmailAuthProvider.credential(user.email, deletePassword);
        await reauthenticateWithCredential(user, credential);
      }
      
      // Call Cloud Function to handle complete deletion
      const functions = getFunctions();
      const deleteUserAccount = httpsCallable(functions, 'deleteUserAccount');
      
      const result = await deleteUserAccount({
        password: deletePassword || null, // Pass password for server-side validation
      });
      
      logAnalyticsEvent('delete_account_success', {
        collections_deleted: result.data.deletedCollections?.length || 0,
        documents_deleted: result.data.totalDocuments || 0,
      });
      
      // Navigate before signing out: signOut's auth-state cascade can race
      // this navigation and strand the user on the (now empty) profile page.
      // The account is already deleted server-side, so the confirmation page
      // is correct even if the local sign-out hiccups.
      navigate('/account-deleted');
      await auth.signOut().catch(() => {});

    } catch (error) {
      // Error details logged server-side in Cloud Function
      
      // Parse error message from Cloud Function
      let errorMessage = 'Failed to delete account. Please try again.';
      
      if (error.code === 'functions/failed-precondition') {
        // Subscription still active - show specific message
        errorMessage = error.message;
      } else if (error.code === 'functions/unauthenticated') {
        errorMessage = 'You must be logged in to delete your account.';
      } else if (error.code === 'functions/invalid-argument') {
        errorMessage = error.message;
      } else if (error.code === 'functions/resource-exhausted') {
        // Rate limited
        errorMessage = error.message;
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = 'Incorrect password. Please try again.';
      }
      
      setSecurityMessage({ type: 'error', text: errorMessage });
      logAnalyticsEvent('delete_account_failure', { 
        error_code: error.code,
        error_message: error.message 
      });
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Get status icon and color based on subscription state
   */
  const getStatusDisplay = () => {
    const state = getSubscriptionState();

    switch (state) {
      case 'grandfathered':
        // Accent, not amber: this is a *good* state (complimentary access), and
        // amber in this product means "these medications may interact
        // dangerously" — DESIGN_DOC §10.
        return {
          icon: <StarIcon sx={{ color: color.accent }} />,
          color: color.accent,
          text: 'Complimentary Access',
          description: `Thank you for being an early user! Enjoy complimentary myStack Membership access${complimentaryUntil ? ` until ${complimentaryUntil}` : ''}.`
        };
      case 'complimentary_expired':
        // Never trial-expired, payment, or reactivation language here: this
        // cohort had complimentary access, not a Stripe subscription.
        return {
          icon: null,
          color: color.inkSoft,
          text: complimentaryEndedOn ? `Complimentary access ended on ${complimentaryEndedOn}` : 'Complimentary access ended',
          description: 'Your saved stacks and data are still here. Start Membership to keep using the advanced features.'
        };
      case 'pro':
        if (isTrialing) {
          return {
            icon: <CheckCircleIcon sx={{ color: color.accent }} />,
            color: color.accent,
            text: 'Status: Free Trial',
            description: `Enjoy unlimited stacks & advanced features until ${formatSubscriptionDate(subscriptionStatus?.trialEndSec, subscriptionStatus?.trialEnd)}`
          };
        } else {
          return {
            icon: <CheckCircleIcon sx={{ color: color.accent }} />,
            color: color.accent,
            text: 'Status: Active',
            description: 'Thank you for being a member. You have full access to unlimited stacks and advanced features.'
          };
        }
      case 'issue':
        // Follows the `complimentary_expired` treatment directly above: a lapsed
        // subscription is a state, not a hazard. Amber is reserved for
        // interaction risk (DESIGN_DOC §10), and the CTA below the status is
        // what carries the call to act.
        return {
          icon: null,
          color: color.inkSoft,
          text: `Status: Ended on ${formatSubscriptionDate(subscriptionStatus?.currentPeriodEndSec, subscriptionStatus?.currentPeriodEnd)}`,
          description: 'Your stacks are missing the advanced analysis for diet and medications.'
        };
      default:
        return {
          icon: null,
          color: color.inkSoft,
          text: 'Status: Free',
          description: 'Unlock advanced recommendations with a free trial'
        };
    }
  };

  const statusDisplay = getStatusDisplay();
  const subscriptionState = getSubscriptionState();

  return (
    <Stack spacing={3}>
      {/* Error Alert */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Personal Information Section */}
      <Card variant="outlined" sx={{ borderRadius: radius.md, order: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: color.ink }}>
              Account & Security
            </Typography>
          </Box>
          
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Email
              </Typography>
              <Typography variant="body1">
                {currentUser?.email || 'Not provided'}
              </Typography>
            </Box>
            
            {/* Account Type - Different display for OAuth (Google/Apple) vs Email */}
            {isOAuthOnlyUser ? (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1.5,
                borderRadius: radius.sm,
                backgroundColor: color.surface,
                border: `${r(1)} solid ${color.line}`
              }}>
                {isGoogleUser ? (
                  <Box
                    component="img"
                    src="https://www.google.com/favicon.ico"
                    alt="Google"
                    sx={{ width: 20, height: 20 }}
                  />
                ) : (
                  <Box component="svg" viewBox="0 0 814 1000" aria-hidden="true" sx={{ width: 20, height: 20 }}>
                    <path fill="#000000" d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.7 40.8-165.9 40.8s-105.6-57-155.5-127C46.7 790.7 0 663 0 541.8c0-194.4 126.4-297.5 250.8-297.5 66.1 0 121.2 43.4 162.7 43.4 39.5 0 101.1-46 176.3-46 28.5 0 130.9 2.6 198.3 99.2zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z" />
                  </Box>
                )}
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: color.ink }}>
                    Signed in with {isGoogleUser ? 'Google' : 'Apple'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {isGoogleUser
                      ? 'Password managed by your Google account'
                      : 'Password managed by your Apple ID'}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Account Type
                  </Typography>
                  <Typography variant="body1">
                    Email Account
                  </Typography>
                </Box>
                <Box sx={{ pt: 1 }}>
                  <Button
                    size="small"
                    startIcon={<SecurityIcon />}
                    onClick={() => setSecurityModalOpen(true)}
                    sx={{ 
                      color: color.inkSoft,
                      textTransform: 'none',
                      fontSize: '0.875rem'
                    }}
                  >
                    Change Password
                  </Button>
                </Box>
              </>
            )}

            {/* Delete Account Link - Subtle red link at bottom of card */}
            {appConfig.FEATURES.ACCOUNT_DELETION_ENABLED && (
              <>
                <Divider sx={{ mt: 2 }} />
                <Box sx={{ pt: 2 }}>
                  <Typography
                    component="span"
                    onClick={() => setDeleteModalOpen(true)}
                    sx={{
                      color: color.destructive,
                      fontSize: '0.813rem',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      opacity: 0.7,
                      '&:hover': {
                        opacity: 1
                      }
                    }}
                  >
                    Delete my account
                  </Typography>
                </Box>
              </>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Divider sx={{ my: 2, display: 'none' }} />

      {/* Subscription Status Section */}
      <Card variant="outlined" sx={{ borderRadius: radius.md, order: 1 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: color.ink }}>
            Subscription
          </Typography>
            {statusDisplay.icon}
          </Box>

          <Stack spacing={2}>
            <Box>
              <Box sx={{ mb: 0.5 }}>
                <Typography variant="body1" sx={{ fontWeight: 500, color: statusDisplay.color }}>
                  {statusDisplay.text}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" data-testid="subscription-description">
                {statusDisplay.description}
              </Typography>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ pt: 1 }}>
              {subscriptionState === 'free' && (
                <MembershipPricingCards
                  source="profile_subscription_section"
                  compact
                  title="Start Your Membership"
                  description="Choose a plan to start your 14-day free trial."
                  cancelPath="/profile?canceled=true"
                />
              )}

              {subscriptionState === 'complimentary_expired' && (
                <MembershipPricingCards
                  source="profile_complimentary_expired"
                  compact
                  title="Continue with Membership"
                  description="Choose a plan to keep using the advanced features."
                  cancelPath="/profile?canceled=true"
                />
              )}

              {(subscriptionState === 'pro' || subscriptionState === 'issue') && !isGrandfathered && (
                <Button
                  variant={subscriptionState === 'issue' ? 'contained' : 'outlined'}
                  onClick={handleManageSubscription}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={16} /> : null}
                  sx={{
                    width: { xs: '100%', sm: 'auto' },
                    borderColor: subscriptionState === 'issue' ? undefined : color.line,
                    color: subscriptionState === 'issue' ? color.accentInk : color.ink,
                    backgroundColor: subscriptionState === 'issue' ? color.accent : undefined,
                    textTransform: 'none',
                    borderRadius: radius.sm,
                    px: 3,
                    py: 1,
                    fontWeight: 500,
                    '&:hover': {
                      borderColor: subscriptionState === 'issue' ? undefined : color.inkMuted,
                      backgroundColor: subscriptionState === 'issue' ? color.accentHover : color.surface
                    }
                  }}
                >
                  {loading ? 'Opening...' : (subscriptionState === 'issue' ? 'Reactivate Now' : 'Manage Subscription')}
                </Button>
              )}

              {isGrandfathered && (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No billing required until expiration.
                </Typography>
              )}
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Security Modal */}
      <Dialog 
        open={securityModalOpen} 
        onClose={() => setSecurityModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter your current password and choose a new one.
          </DialogContentText>
          
          {securityMessage.text && (
            <Alert severity={securityMessage.type} sx={{ mb: 2 }}>
              {securityMessage.text}
            </Alert>
          )}

          <Stack spacing={2}>
            <TextField
              name="currentPassword"
              label="Current Password"
              type="password"
              fullWidth
              value={passwordState.currentPassword}
              onChange={(e) => setPasswordState(prev => ({ ...prev, currentPassword: e.target.value }))}
            />
            <TextField
              name="newPassword"
              label="New Password"
              type="password"
              fullWidth
              value={passwordState.newPassword}
              onChange={(e) => setPasswordState(prev => ({ ...prev, newPassword: e.target.value }))}
            />
            <TextField
              name="confirmNewPassword"
              label="Confirm New Password"
              type="password"
              fullWidth
              value={passwordState.confirmNewPassword}
              onChange={(e) => setPasswordState(prev => ({ ...prev, confirmNewPassword: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSecurityModalOpen(false)}>Cancel</Button>
          <Button 
            onClick={handlePasswordChange} 
            variant="contained"
            disabled={isProcessing || !passwordState.currentPassword || !passwordState.newPassword || !passwordState.confirmNewPassword}
          >
            {isProcessing ? 'Updating...' : 'Update Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Account Dialog */}
      <Dialog
        open={deleteModalOpen}
        onClose={() => !isProcessing && setDeleteModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{
          color: color.destructive,
          fontWeight: 600,
          borderBottom: `${r(1)} solid ${color.line}`,
          pb: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon />
            Delete Account Permanently
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {/* Loading State */}
          {isProcessing && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
              <CircularProgress size={48} sx={{ mb: 2 }} />
              <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                Deleting your account...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This may take a few moments
              </Typography>
            </Box>
          )}

          {!isProcessing && (
            <>
              {/* Eligibility preflight - neutral while checking */}
              {isCheckingDeletionEligibility && (
                <Alert severity="info" icon={<CircularProgress size={20} />} sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    Checking your subscription status…
                  </Typography>
                </Alert>
              )}

              {/* Subscription Warning - only after the check finds an open subscription */}
              {!isCheckingDeletionEligibility && hasBlockingSubscriptionForDeletion() && (
              <Alert severity="error" sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Cannot delete account with an open subscription
                </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {deletionEligibility.message || 'Please cancel or resolve your subscription before deleting your account.'}
                </Typography>
          <Button
                    size="small"
                    variant="outlined"
            onClick={() => {
              setDeleteModalOpen(false);
                      handleManageSubscription();
            }}
            sx={{
                      mt: 1,
                      // Accent, not destructive: managing a subscription is a
                      // navigation, and nothing here destroys anything. The
                      // surrounding Alert already carries the error severity —
                      // red on the button as well would read as "this button
                      // deletes" (DESIGN_DOC §10).
                      borderColor: color.accent,
                      color: color.accent,
              textTransform: 'none',
                      '&:hover': {
                        borderColor: color.accentHover,
                        backgroundColor: alpha(color.accent, 0.04)
                      }
                    }}
                  >
                    Manage Subscription
                  </Button>
            </Alert>
          )}

              {/* Only show deletion UI if user can delete */}
              {!hasBlockingSubscriptionForDeletion() && (
                <>
                  {/* General Warning */}
              <Alert severity="error" sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  This action cannot be undone!
                </Typography>
                <Typography variant="body2">
                  All your data will be permanently deleted.
                </Typography>
              </Alert>
          
              {/* Error Messages */}
          {securityMessage.text && (
            <Alert severity={securityMessage.type} sx={{ mb: 2 }}>
              {securityMessage.text}
            </Alert>
          )}

              {/* Password Input for Email/Password Users */}
          {hasPasswordProvider && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    To confirm, please enter your password:
                  </Typography>
            <TextField
                    label="Password"
              type="password"
              fullWidth
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
                        autoFocus={hasPasswordProvider}
                    placeholder="Enter your password"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: radius.sm
                      }
                    }}
            />
                </Box>
          )}
          
                  {/* Type DELETE Confirmation */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      To confirm deletion, please type <Box component="span" sx={{ fontWeight: 700, color: color.destructive }}>DELETE</Box> below:
                  </Typography>
                  <TextField
                      label="Type DELETE to confirm"
                    fullWidth
                      value={deleteConfirmation}
                      onChange={(e) => setDeleteConfirmation(e.target.value)}
                      autoFocus={isOAuthOnlyUser}
                      placeholder="DELETE"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: radius.sm
                      }
                    }}
                  />
                </Box>

              {/* Final Confirmation */}
              <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary', fontStyle: 'italic' }}>
                This action is immediate and cannot be reversed.
              </Typography>
            </>
          )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
          <Button 
            onClick={() => {
              setDeleteModalOpen(false);
              setDeletePassword('');
              setDeleteConfirmation('');
              setSecurityMessage({ type: '', text: '' });
            }}
            disabled={isProcessing}
            sx={{
              borderRadius: radius.sm,
              textTransform: 'none',
              fontWeight: 500,
              px: 3
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteAccount} 
            color="error" 
            disabled={
              isProcessing || 
              (hasPasswordProvider && !deletePassword) ||
              deleteConfirmation !== 'DELETE' ||
              hasBlockingSubscriptionForDeletion()
            }
            variant="contained"
            sx={{
              borderRadius: radius.sm,
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1
            }}
          >
            {isProcessing ? 'Deleting Account...' : 'Yes, Delete My Account'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

export default AccountSubscriptionSection; 
